import { GetAccountsByCurrentUserMerchantHttpResponse, MerchantAccountHttpResponse } from '../../core/models/mxp-account.model';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MerchantTypeEnum } from '@rezolved/mxp-proto';
import { MxpSnackBarService } from '../../core/services/mxp-snackbar.service';
import { MerchantService } from '../../core/services/mxp-merchant.service';
import { take } from 'rxjs/operators';

const ACCOUNT_ALREADY_EXISTS_ERROR_CODE = 4000;
const ACCOUNT_VALIDATION_NOT_ALLOWED_ERROR_CODE = 4001;
const BANXICO_ACCOUNT_VALIDATION_ERROR_CODE = 2001;

@Component({
  selector: 'mxp-accounts',
  templateUrl: './accounts.component.html',
  styleUrls: ['./accounts.component.scss'],
})
export class AccountsComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;

  accounts: AccountTableItem[] = [];
  dataSource = new MatTableDataSource<AccountTableItem>([]);
  isLoading = false;
  isEnrollAccountLoading = false;
  isRetryAccountValidationLoading = false;
  searchTerm = '';
  filteredAccountsLength = 0;
  currentPage = 0;
  itemsPerPage = 10;
  form = new FormGroup({
    accountNumber: new FormControl('', Validators.compose([Validators.required, Validators.pattern('^[0-9]{18}$')])),
  });
  pageSizeOptions = [10, 25, 50, 100];
  displayedColumns = [
    'createdOn',
    'accountNumber',
    'validationAttempts',
    'validationStatus',
    'actions',
  ];

  constructor(
    private readonly http: HttpClient,
    private readonly mxpSnackBarService: MxpSnackBarService,
    private readonly merchantService: MerchantService,
  ) {}

  ngOnInit(): void {
    this.loadAccounts();
  }

  ngAfterViewInit(): void {
    this.sort.sortChange.subscribe(() => {
      this.currentPage = 0;
      this.updateDataSource();
    });
  }

  loadAccounts(showSnackbar = true): void {
    this.isLoading = true;

    this.mxpSnackBarService
      .wrapApiCall(
        'SNACKBAR.GET_ACCOUNTS',
        this.http.get<GetAccountsByCurrentUserMerchantHttpResponse>(`${environment.identityUrl}/api/mxp-accounts/current-user-merchant`),
        undefined,
        undefined,
        undefined,
        !showSnackbar,
      )
      .subscribe(
        (response: GetAccountsByCurrentUserMerchantHttpResponse) => {
          this.accounts = (response.accounts || []).map((account) => this.toAccountTableItem(account));
          this.currentPage = 0;
          this.updateDataSource();
          this.isLoading = false;
        },
        (error) => {
          console.log(error);
          this.isLoading = false;
        },
      );
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.updateDataSource();
  }

  onPageSizeChanged(pageSize: number): void {
    this.itemsPerPage = pageSize;
    this.currentPage = 0;
    this.updateDataSource();
  }

  applySearchTerm(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 0;
    this.updateDataSource();
  }

  enrollAccount(): void {
    if (this.form.invalid || this.isEnrollAccountLoading) {
      this.form.markAllAsTouched();
      return;
    }

    const accountNumber: string = this.form.value.accountNumber;

    this.isEnrollAccountLoading = true;
    this.merchantService.merchant$.pipe(take(1)).subscribe((merchant) => {
      if (merchant?.merchantType === MerchantTypeEnum.TRADITIONAL) {
        this.addAccount(accountNumber);
        return;
      } else {
        this.enrollAccountWithBanxico(accountNumber);
      }
    });
  }

  retryAccountValidation(account: AccountTableItem): void {
    if (!account.accountId || this.isRetryAccountValidationLoading) {
      return;
    }

    this.isRetryAccountValidationLoading = true;

    this.http
      .post<RetryAccountValidationHttpResponse>(
        `${environment.identityUrl}/api/mxp-accounts/retry-account-validation`,
        { accountId: account.accountId },
      )
      .subscribe(
        (response: RetryAccountValidationHttpResponse) => {
          this.isRetryAccountValidationLoading = false;

          if (response?.accountValidationStatus) {
            this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.RETRY_ACCOUNT_VALIDATION');
            this.loadAccounts(false);
            return;
          }

          this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.RETRY_ACCOUNT_VALIDATION_FAIL');
        },
        (error: HttpErrorResponse) => {
          this.isRetryAccountValidationLoading = false;
          const errorCode = Number(error.error?.errorCode);

          if (errorCode === ACCOUNT_VALIDATION_NOT_ALLOWED_ERROR_CODE) {
            this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.RETRY_ACCOUNT_VALIDATION_NOT_ALLOWED');
            return;
          }

          if (errorCode === BANXICO_ACCOUNT_VALIDATION_ERROR_CODE) {
            this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.RETRY_ACCOUNT_VALIDATION_FAILED_RESULT');
            this.loadAccounts(false);
            return;
          }

          this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.RETRY_ACCOUNT_VALIDATION_FAIL');
        },
      );
  }

  private updateDataSource(): void {
    const start = this.currentPage * this.itemsPerPage;
    const accounts = this.getSortedAccounts();
    this.filteredAccountsLength = accounts.length;
    this.dataSource.data = accounts.slice(start, start + this.itemsPerPage);
  }

  private getSortedAccounts(): AccountTableItem[] {
    const direction = this.sort?.direction;
    const active = this.sort?.active as keyof AccountTableItem | undefined;

    if (!active || !direction) {
      return this.getFilteredAccounts();
    }

    return this.getFilteredAccounts().sort((left, right) => {
      const result = this.compare(left[active], right[active]);
      return direction === 'asc' ? result : -result;
    });
  }

  private getFilteredAccounts(): AccountTableItem[] {
    const searchTerm = this.searchTerm.trim().toLowerCase();

    if (!searchTerm) {
      return [...this.accounts];
    }

    return this.accounts.filter((account) =>
      [
        account.accountNumber,
        account.validationAttempts?.toString(),
        account.validationStatusTranslationKey,
        account.validationStatus,
        account.createdOn?.toLocaleDateString(),
      ].some((value) => value?.toLowerCase().includes(searchTerm)),
    );
  }

  private compare(left?: string | number | Date, right?: string | number | Date): number {
    if (left == null && right == null) {
      return 0;
    }

    if (left == null) {
      return -1;
    }

    if (right == null) {
      return 1;
    }

    if (left instanceof Date && right instanceof Date) {
      return left.getTime() - right.getTime();
    }

    if (typeof left === 'number' && typeof right === 'number') {
      return left - right;
    }

    return left.toString().localeCompare(right.toString());
  }

  private toAccountTableItem(account: MerchantAccountHttpResponse): AccountTableItem {
    return {
      accountId: account.accountId,
      accountNumber: account.accountNumber ?? undefined,
      validationAttempts: account.validationAttempts,
      validationStatus: account.validationStatus,
      validationStatusTranslationKey: this.getValidationStatusTranslationKey(account.validationStatus),
      createdOn: account.createdOn ? new Date(account.createdOn) : undefined,
    };
  }

  private getValidationStatusTranslationKey(validationStatus?: string): string | undefined {
    switch (validationStatus) {
      case 'ValidationPending':
        return 'ACCOUNTS_TABLE.VALIDATION_STATUS_PENDING';
      case 'ValidationSucceded':
        return 'ACCOUNTS_TABLE.VALIDATION_STATUS_SUCCEDED';
      case 'ValidationFailed':
        return 'ACCOUNTS_TABLE.VALIDATION_STATUS_FAILED';
      default:
        return undefined;
    }
  }

  private addAccount(accountNumber: string): void {
    debugger;
    this.http.post<AddAccountHttpResponse>(`${environment.identityUrl}/api/mxp-accounts/add-account`,{ accountNumber },).subscribe(
      (response) => this.handleAccountCreated(response?.accountId),
      (error) => this.handleEnrollAccountError(error),
    );
  }

  private enrollAccountWithBanxico(accountNumber: string): void {
    this.http.post<EnrollAccountHttpResponse>(
      `${environment.identityUrl}/api/mxp-accounts/enroll-account`,
      { accountNumber },
    ).subscribe(
      (response) => this.handleAccountCreated(response?.accountId),
      (error) => this.handleEnrollAccountError(error),
    );
  }

  private handleAccountCreated(accountId?: string): void {
    this.isEnrollAccountLoading = false;

    if (accountId) {
      this.form.reset();
      this.form.markAsPristine();
      this.form.markAsUntouched();
      this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.ENROLL_ACCOUNT');
      this.loadAccounts(false);
      return;
    }

    this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.ENROLL_ACCOUNT_FAIL');
  }

  private handleEnrollAccountError(error: HttpErrorResponse): void {
    console.log(error);
    this.isEnrollAccountLoading = false;
    const errorCode = error.error?.errorCode;
    if (Number(errorCode) === ACCOUNT_ALREADY_EXISTS_ERROR_CODE) {
      this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.ENROLL_ACCOUNT_ALREADY_EXISTS');
      return;
    }

    this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.ENROLL_ACCOUNT_FAIL');
  }
}

interface AccountTableItem {
  accountId?: string;
  accountNumber?: string;
  validationAttempts?: number;
  validationStatus?: string;
  validationStatusTranslationKey?: string;
  createdOn?: Date;
}

interface RetryAccountValidationHttpResponse {
  accountValidationStatus: string;
}

interface EnrollAccountHttpResponse {
  accountId: string;
}

interface AddAccountHttpResponse {
  accountId: string;
}
